import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';

const rosterClient = createHttpClient(serviceUrls.roster);

export interface RosterClass {
  id: string;
  tenant_id: string;
  name: string;
  code?: string;
  grade?: string;
  academic_year_key?: string;
  status: string;
  primary_teacher_platform_user_id?: string | null;
  secondary_teacher_platform_user_ids?: string[];
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
}

export interface RosterSection {
  id: string;
  tenant_id: string;
  class_id: string;
  name: string;
  display_name?: string;
  capacity?: number;
  academic_year_key?: string;
  teacher_platform_user_ids?: string[];
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
}

export interface RosterEnrollment {
  id: string;
  tenant_id: string;
  student_id: string;
  class_id: string;
  section_id?: string;
  academic_year_key?: string;
  status: string;
  effective_from?: string | null;
  effective_to?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
}

interface PagingResponse<T> {
  items: T[];
  total?: number;
  limit?: number;
  skip?: number;
}

export const listClasses = async (params?: {
  limit?: number;
  skip?: number;
  /** Override tenant (defaults from auth interceptors / localStorage). */
  tenantId?: string;
}): Promise<RosterClass[]> => {
  const { tenantId, limit, skip } = params || {};
  const response = await rosterClient.get<PagingResponse<RosterClass>>('/v1/roster/classes', {
    params: { limit, skip },
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId.trim() } } : {}),
  });
  return response.data.items;
};

export const getClass = async (classId: string): Promise<RosterClass> => {
  const response = await rosterClient.get<RosterClass>(`/v1/roster/classes/${classId}`);
  return response.data;
};

export interface CreateClassPayload {
  name: string;
  code?: string;
  grade?: string;
  academic_year_key?: string;
}

export const createClass = async (payload: CreateClassPayload): Promise<RosterClass> => {
  const response = await rosterClient.post<RosterClass>('/v1/roster/classes', payload);
  return response.data;
};

/** POST /v1/roster/classes/bulk */
export interface RosterBulkCreateClassItem {
  name: string;
  grade: string;
  academic_year_key?: string;
  status?: string;
}

export interface RosterBulkCreateClassesPayload {
  classes: RosterBulkCreateClassItem[];
  skip_duplicates: boolean;
  continue_on_error: boolean;
}

export interface RosterBulkCreateClassesSummary {
  requested: number;
  created: number;
  skipped: number;
  failed: number;
}

export interface RosterBulkCreateClassResultRow {
  index: number;
  status: string;
  name?: string;
  class_id?: string;
  error_code?: string;
  message?: string;
}

export interface RosterBulkCreateClassesResponse {
  summary: RosterBulkCreateClassesSummary;
  results: RosterBulkCreateClassResultRow[];
}

export interface RosterBulkCreateClasses422Body {
  errors?: unknown[];
}

export const bulkCreateClasses = async (
  payload: RosterBulkCreateClassesPayload
): Promise<RosterBulkCreateClassesResponse> => {
  const response = await rosterClient.post<RosterBulkCreateClassesResponse>('/v1/roster/classes/bulk', payload);
  return response.data;
};

export interface UpdateClassPayload {
  name?: string;
}

export const patchClass = async (classId: string, payload: UpdateClassPayload): Promise<RosterClass> => {
  const response = await rosterClient.patch<RosterClass>(`/v1/roster/classes/${classId}`, payload);
  return response.data;
};

export const listSections = async (params?: { limit?: number; skip?: number; tenantId?: string }): Promise<RosterSection[]> => {
  const { tenantId, limit, skip } = params || {};
  const response = await rosterClient.get<PagingResponse<RosterSection>>('/v1/roster/sections', {
    params: { limit, skip },
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId.trim() } } : {}),
  });
  return response.data.items;
};

export const getSection = async (sectionId: string): Promise<RosterSection> => {
  const response = await rosterClient.get<RosterSection>(`/v1/roster/sections/${sectionId}`);
  return response.data;
};

export interface CreateSectionPayload {
  class_id: string;
  name: string;
  display_name?: string;
  capacity?: number;
  academic_year_key?: string;
}

export const createSection = async (payload: CreateSectionPayload): Promise<RosterSection> => {
  const response = await rosterClient.post<RosterSection>('/v1/roster/sections', payload);
  return response.data;
};

export interface UpdateSectionPayload {
  name?: string;
  capacity?: number;
}

export const patchSection = async (sectionId: string, payload: UpdateSectionPayload): Promise<RosterSection> => {
  const response = await rosterClient.patch<RosterSection>(`/v1/roster/sections/${sectionId}`, payload);
  return response.data;
};

export const listEnrollments = async (params?: { limit?: number; skip?: number }): Promise<RosterEnrollment[]> => {
  const response = await rosterClient.get<PagingResponse<RosterEnrollment>>('/v1/roster/enrollments', {
    params,
  });
  return response.data.items;
};

export const getEnrollment = async (enrollmentId: string): Promise<RosterEnrollment> => {
  const response = await rosterClient.get<RosterEnrollment>(`/v1/roster/enrollments/${enrollmentId}`);
  return response.data;
};

export interface CreateEnrollmentPayload {
  student_id: string;
  class_id: string;
  section_id?: string;
  academic_year_key: string;
}

export const createEnrollment = async (payload: CreateEnrollmentPayload): Promise<RosterEnrollment> => {
  const response = await rosterClient.post<RosterEnrollment>('/v1/roster/enrollments', payload);
  return response.data;
};

/** Nested class on student payloads (e.g. `/v1/roster/students/me`). */
export interface RosterStudentClass {
  id?: string;
  name?: string;
  grade?: string;
  academic_year_key?: string;
}

const RESOLVED_GRADE_MIN = 1;
const RESOLVED_GRADE_MAX = 12;

/**
 * Effective grade for tutor and similar UIs: prefer enrolled class, then top-level grade.
 * Returns null if missing or not a sane integer (1–12).
 */
export function resolveStudentGrade(student: RosterStudent): number | null {
  const fromClass = student.class?.grade;
  const fromStudent = student.grade;
  const raw =
    fromClass != null && String(fromClass).trim() !== ''
      ? fromClass
      : fromStudent != null && String(fromStudent).trim() !== ''
        ? fromStudent
        : null;
  if (raw == null) return null;
  const s = String(raw).trim();
  const direct = parseInt(s, 10);
  if (Number.isFinite(direct) && direct >= RESOLVED_GRADE_MIN && direct <= RESOLVED_GRADE_MAX) return direct;
  // e.g. "Grade 2", "G2", "Class 10" — align with ai-tutor-service roster_client.parse_roster_grade_value
  const m = s.match(/(?:^|[^\d])(1[0-2]|[1-9])(?:[^\d]|$)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n < RESOLVED_GRADE_MIN || n > RESOLVED_GRADE_MAX) return null;
  return n;
}

export interface RosterStudent {
  id: string;
  _id?: string;
  tenant_id: string;
  admission_no?: string;
  roll_no?: string | null;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  gender?: string;
  dob?: string;
  phone?: string;
  email?: string;
  address?: string;
  guardian_name?: string;
  grade?: string;
  class?: RosterStudentClass;
  platform_user_id?: string;
  parent_user_ids?: string[];
  status?: string;
  invite_id?: string;
  invited_at?: string | null;
  accepted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
}

export interface RosterTeacher {
  id: string;
  _id?: string;
  tenant_id: string;
  platform_user_id?: string | null;
  status?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  employment?: {
    employee_code?: string;
    joining_date?: string;
  };
  notes?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
}

export interface CreateStudentPayload {
  first_name: string;
  last_name?: string;
  display_name?: string;
  email?: string;
  phone?: string;
  admission_no: string;
  gender?: string;
  dob?: string;
}

export interface CreateTeacherPayload {
  display_name: string;
  email: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  employee_code?: string;
  joining_date?: string;
  notes?: string;
}

export const listStudents = async (params?: {
  limit?: number;
  skip?: number;
  tenantId?: string;
}): Promise<RosterStudent[]> => {
  const { tenantId, limit, skip } = params || {};
  const response = await rosterClient.get<PagingResponse<RosterStudent>>('/v1/roster/students', {
    params: { limit, skip },
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId.trim() } } : {}),
  });
  return response.data.items;
};

export const getStudent = async (studentId: string, params?: { tenantId?: string }): Promise<RosterStudent> => {
  const tenantId = params?.tenantId?.trim();
  const response = await rosterClient.get<RosterStudent>(`/v1/roster/students/${studentId}`, {
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId } } : {}),
  });
  return response.data;
};

export const createStudent = async (payload: CreateStudentPayload): Promise<RosterStudent> => {
  const response = await rosterClient.post<RosterStudent>('/v1/roster/students', payload);
  return response.data;
};

/** POST /v1/roster/students/bulk — roster records only (no invites, parents, or enrollments). */
export interface RosterBulkCreateStudentItem {
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  admission_no?: string;
  gender?: string;
  dob?: string;
}

export interface RosterBulkCreateStudentsPayload {
  students: RosterBulkCreateStudentItem[];
  skip_duplicates: boolean;
  continue_on_error: boolean;
}

export interface RosterBulkCreateStudentsSummary {
  requested: number;
  created: number;
  skipped: number;
  failed: number;
}

export interface RosterBulkCreateStudentResultRow {
  index: number;
  status: string;
  name?: string;
  student_id?: string;
  error_code?: string;
  message?: string;
}

export interface RosterBulkCreateStudentsResponse {
  summary: RosterBulkCreateStudentsSummary;
  results: RosterBulkCreateStudentResultRow[];
}

export interface RosterBulkCreateStudents422Body {
  errors?: unknown[];
}

export const bulkCreateStudents = async (
  payload: RosterBulkCreateStudentsPayload
): Promise<RosterBulkCreateStudentsResponse> => {
  const response = await rosterClient.post<RosterBulkCreateStudentsResponse>('/v1/roster/students/bulk', payload);
  return response.data;
};

/** POST /v1/roster/teachers/bulk — roster records only (no invites). */
export interface RosterBulkCreateTeacherItem {
  display_name: string;
  email: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  employee_code?: string;
  joining_date?: string;
  notes?: string;
}

export interface RosterBulkCreateTeachersPayload {
  teachers: RosterBulkCreateTeacherItem[];
  skip_duplicates: boolean;
  continue_on_error: boolean;
}

export interface RosterBulkCreateTeachersSummary {
  requested: number;
  created: number;
  skipped: number;
  failed: number;
}

export interface RosterBulkCreateTeacherResultRow {
  index: number;
  status: string;
  display_name?: string;
  email?: string;
  teacher_id?: string;
  error_code?: string;
  message?: string;
}

export interface RosterBulkCreateTeachersResponse {
  summary: RosterBulkCreateTeachersSummary;
  results: RosterBulkCreateTeacherResultRow[];
}

export interface RosterBulkCreateTeachers422Body {
  errors?: unknown[];
}

export const bulkCreateTeachers = async (
  payload: RosterBulkCreateTeachersPayload
): Promise<RosterBulkCreateTeachersResponse> => {
  const response = await rosterClient.post<RosterBulkCreateTeachersResponse>('/v1/roster/teachers/bulk', payload);
  return response.data;
};

export const listTeachers = async (params?: {
  limit?: number;
  skip?: number;
  include?: string;
  tenantId?: string;
}): Promise<RosterTeacher[]> => {
  const { tenantId, limit, skip, include } = params || {};
  const response = await rosterClient.get<PagingResponse<RosterTeacher>>('/v1/roster/teachers', {
    params: { limit, skip, include },
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId.trim() } } : {}),
  });
  return response.data.items;
};

export const getTeacher = async (teacherId: string, params?: { [key: string]: any }): Promise<any> => {
  const response = await rosterClient.get(`/v1/roster/teachers/${teacherId}`, {
    params,
  });
  return response.data;
};

export const createTeacher = async (payload: CreateTeacherPayload): Promise<RosterTeacher> => {
  const response = await rosterClient.post<RosterTeacher>('/v1/roster/teachers', payload);
  return response.data;
};

export interface PatchTeacherPayload {
  display_name?: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  employment?: { employee_code?: string; joining_date?: string };
  notes?: string;
}

export const patchTeacher = async (teacherId: string, payload: PatchTeacherPayload): Promise<RosterTeacher> => {
  const response = await rosterClient.patch<RosterTeacher>(`/v1/roster/teachers/${teacherId}`, payload);
  return response.data;
};

export const deleteTeacher = async (teacherId: string): Promise<void> => {
  await rosterClient.delete(`/v1/roster/teachers/${teacherId}`);
};

export interface AssignTeacherToClassPayload {
  teacher_profile_id: string;
  role: 'primary' | 'secondary';
}

export const assignTeacherToClass = async (
  classId: string,
  payload: AssignTeacherToClassPayload
): Promise<void> => {
  await rosterClient.post(`/v1/roster/classes/${classId}/teachers`, payload);
};

export const unassignTeacherFromClass = async (
  classId: string,
  teacherPlatformUserId: string
): Promise<void> => {
  await rosterClient.delete(`/v1/roster/classes/${classId}/teachers/${teacherPlatformUserId}`);
};

export interface ParentPersonal {
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
}

export interface RosterParent {
  id: string;
  tenant_id: string;
  platform_user_id?: string | null;
  status?: string;
  personal: ParentPersonal;
  notes?: string | null;
  invite_id?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  created_by?: string;
  updated_by?: string;
  deleted_by?: string | null;
}

export interface CreateParentPayload {
  personal: ParentPersonal;
  notes?: string | null;
}

export const listParents = async (params?: { limit?: number; skip?: number }): Promise<RosterParent[]> => {
  const response = await rosterClient.get<PagingResponse<RosterParent>>('/v1/roster/parents', { params });
  return response.data.items;
};

export const createParent = async (payload: CreateParentPayload): Promise<RosterParent> => {
  const response = await rosterClient.post<RosterParent>('/v1/roster/parents', payload);
  return response.data;
};

export interface InviteParentPayload {
  email: string;
  invitee_name: string;
  notes?: string;
  student_id: string;
  metadata?: {
    relationship?: string;
  };
}

export const inviteParent = async (parentId: string, payload: InviteParentPayload): Promise<any> => {
  const response = await rosterClient.post(`/v1/roster/parents/${parentId}/invite`, payload);
  return response.data;
};

export const assignTeacherToSection = async (sectionId: string, teacherProfileId: string): Promise<void> => {
  await rosterClient.post(`/v1/roster/sections/${sectionId}/teachers`, {
    teacher_profile_id: teacherProfileId,
  });
};

/**
 * Current user's roster student row (`GET /v1/roster/students/me`).
 * Server returns 403 "Student role required" for non-students — prefer `useStudentMe` or
 * `shouldFetchStudentRosterMe` before calling from shared UI.
 */
/** Dedupe parallel callers (dashboard mounts several panels at once). React Query is still preferred. */
let studentMeInFlight: Promise<RosterStudent> | null = null;

export const getStudentMe = async (): Promise<RosterStudent> => {
  if (studentMeInFlight) return studentMeInFlight;
  studentMeInFlight = rosterClient
    .get<RosterStudent>('/v1/roster/students/me')
    .then((response) => response.data)
    .finally(() => {
      studentMeInFlight = null;
    });
  return studentMeInFlight;
};

export const patchStudentMe = async (payload: Partial<RosterStudent>, studentId?: string): Promise<RosterStudent> => {
  try {
    const response = await rosterClient.patch<RosterStudent>('/v1/roster/students/me', payload);
    return response.data;
  } catch (err) {
    if (studentId) {
      const response = await rosterClient.patch<RosterStudent>(`/v1/roster/students/${studentId}`, payload);
      return response.data;
    }
    throw err;
  }
};

// Parent: my children (linked students)
export interface ParentChildSection {
  id: string;
  name: string;
  academic_year_key: string;
  class_id: string;
}

export interface ParentChildClass {
  id: string;
  name: string;
  grade: string;
  academic_year_key: string;
}

export interface ParentChild {
  id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  admission_no: string;
  grade: string | null;
  section_id: string;
  class_id: string;
  section: ParentChildSection;
  class: ParentChildClass;
  status: string;
}

export const getMyChildren = async (): Promise<ParentChild[]> => {
  const response = await rosterClient.get<ParentChild[]>('/v1/roster/me/children');
  return Array.isArray(response.data) ? response.data : [];
};

// Parent dashboard types and client
import type { TutorAnalyticsSummary, SpeakingMetricsDoc, DailyEngagementDoc, TrendPoint } from '../../../tutor/types/tutorAnalytics';

export interface ParentDashboardTutor {
  total_tutor_minutes: number;
  sessions_count: number;
  last_session_at?: string | null;
  latest_speaking_level?: string | null;
  latest_overall_score?: number | null;
  last_lesson_no?: number | null;
  last_topic_label?: string | null;
  trend?: TrendPoint[];
  recent_speaking_sessions?: SpeakingMetricsDoc[];
  daily_engagement?: DailyEngagementDoc[];
}

export interface ParentChildDashboard {
  child: ParentChild | RosterStudent;
  teachers: RosterTeacher[];
  learning?: any;
  assignments?: any[];
  tutor: ParentDashboardTutor | null;
}

/**
 * Get the parent dashboard for a specific child.
 * The backend returns an enriched payload: { child, teachers, learning, assignments, tutor }.
 * This function adapts the backend tutor shape into the existing TutorAnalyticsSummary
 * so UI components can be reused.
 */
export const getParentChildDashboard = async (childId: string): Promise<{
  child: ParentChild | RosterStudent;
  teachers: RosterTeacher[];
  learning?: any;
  assignments?: any[];
  tutor: TutorAnalyticsSummary;
}> => {
  const response = await rosterClient.get<ParentChildDashboard>(`/v1/roster/parents/me/children/${childId}/dashboard`);
  const data = response.data || ({} as ParentChildDashboard);

  const t = data.tutor || ({} as ParentDashboardTutor);
  // Normalize recent_speaking_sessions shape into speaking_sessions expected by TutorAnalyticsSummary
  const speakingSessions = (t.recent_speaking_sessions ?? []).map((s: any) => {
    return {
      session_id: s.session_id ?? s.sessionId ?? '',
      tenant_id: s.tenant_id ?? s.tenantId,
      student_id: s.student_id ?? s.studentId,
      created_at: s.created_at ?? s.createdAt ?? null,
      duration_seconds: s.duration_seconds ?? s.durationSeconds ?? 0,
      transcript_excerpt: s.transcript_excerpt ?? s.transcriptExcerpt ?? '',
      scores: {
        pronunciation: s.pronunciation ?? s.pronunciation_score ?? 0,
        fluency: s.fluency ?? s.fluency_score ?? 0,
        grammar: s.grammar ?? s.grammar_score ?? 0,
        vocabulary: s.vocabulary ?? s.vocabulary_score ?? 0,
        overall: s.overall_score ?? s.overall ?? 0,
      },
      speaking_level: s.speaking_level ?? s.speakingLevel ?? null,
      feedback_ta: s.feedback_ta ?? s.feedbackTa ?? '',
      recommendations: s.recommendations ?? [],
    } as any;
  });

  const adapted: TutorAnalyticsSummary = {
    ok: true,
    total_tutor_minutes: t.total_tutor_minutes ?? 0,
    sessions_count: t.sessions_count ?? 0,
    last_active_at: t.last_session_at ?? null,
    latest_speaking_level: t.latest_speaking_level ?? null,
    latest_overall_score: t.latest_overall_score ?? null,
    last_lesson_no: t.last_lesson_no ?? null,
    last_topic_label: t.last_topic_label ?? null,
    trend: t.trend ?? [],
    speaking_sessions: speakingSessions,
    daily_engagement: t.daily_engagement ?? [],
    session_history: [],
  };

  return {
    child: data.child,
    teachers: data.teachers ?? [],
    learning: data.learning,
    assignments: data.assignments ?? [],
    tutor: adapted,
  };
};

export type RosterDashboardClassDetail = {
  class_id: string;
  name: string;
  student_count: number;
};

export type RosterGradeDistribution = {
  grade: number;
  classes: number;
  sections: number;
  students: number;
  class_details: RosterDashboardClassDetail[];
};

export type RosterTeacherWorkload = {
  teacher_id: string;
  display_name: string;
  section_labels: string[];
  student_count: number;
  over_capacity: boolean;
};

export type RosterDashboardSummary = {
  ok: boolean;
  tenant_id: string;
  distinct_grades: number;
  counts: {
    classes: number;
    sections: number;
    students: number;
    teachers: number;
    parents: number;
  };
  grade_distribution: RosterGradeDistribution[];
  teacher_workload: RosterTeacherWorkload[];
  grades_without_teachers: number;
};

export const getRosterDashboardSummary = async (): Promise<RosterDashboardSummary> => {
  const response = await rosterClient.get<RosterDashboardSummary>('/v1/roster/dashboard-summary');
  return response.data;
};
